import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Subtitle1,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { RequestDetail } from '@lg/core';
import { hasAnyRole } from '@lg/shared';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { dateTime } from '../ui/format';
import { DeviationTable } from './DeviationTable';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  gate: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  num: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  spacer: { flex: 1 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS },
  history: { margin: 0, paddingLeft: tokens.spacingHorizontalXL, color: tokens.colorNeutralForeground2 },
});

type GateState = 'done' | 'current' | 'waiting' | 'failed';
const STATE_BADGE: Record<
  GateState,
  { color: 'success' | 'brand' | 'informative' | 'danger'; text: string }
> = {
  done: { color: 'success', text: 'Passed' },
  current: { color: 'brand', text: 'Waiting for decision' },
  waiting: { color: 'informative', text: 'Not reached' },
  failed: { color: 'danger', text: 'Not passing' },
};

/** The four readiness gates (spec 3 flow, 7, 8.2). */
export function ApprovalsTab({ detail, onStarted }: { detail: RequestDetail; onStarted: () => void }) {
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  const r = detail.request;
  const refresh = (d?: RequestDetail) => {
    if (d) qc.setQueryData(['request', r.id], d);
    void qc.invalidateQueries({ queryKey: ['request', r.id] });
    void qc.invalidateQueries({ queryKey: ['requests'] });
    void qc.invalidateQueries({ queryKey: ['audit', r.id] });
  };
  const failure = useQuery({
    queryKey: ['demoFailure'],
    queryFn: () => api.demoFailure?.get() ?? Promise.resolve(null),
    enabled: !!api.demoFailure,
  });

  const pastReview = ['Approved', 'Provisioning', 'NeedsAttention', 'Validating', 'Ready'].includes(r.status);
  const gate1: GateState =
    r.status === 'Draft'
      ? detail.gate1.passed
        ? 'current'
        : 'failed'
      : r.status === 'Cancelled'
        ? 'waiting'
        : 'done';
  const gate2: GateState =
    r.status === 'InReview' && r.currentGate === 2
      ? 'current'
      : (r.status === 'InReview' && r.currentGate === 3) || pastReview
        ? 'done'
        : 'waiting';
  const gate3: GateState =
    r.status === 'InReview' && r.currentGate === 3 ? 'current' : pastReview ? 'done' : 'waiting';
  const gate4: GateState =
    r.status === 'Approved'
      ? 'current'
      : ['Provisioning', 'NeedsAttention', 'Validating', 'Ready'].includes(r.status)
        ? 'done'
        : 'waiting';

  const isOwner = user.upn === r.requesterUpn;
  const history = (gate: number) =>
    detail.approvals
      .filter((a) => a.gate === gate)
      .map((a) => (
        <li key={a.id}>
          <b>{a.decision === 'Approved' ? 'Approved' : 'Sent back'}</b> by {a.approverUpn} ·{' '}
          {dateTime(a.decidedAt)} · version {a.requestVersion}
          {a.requestVersion !== r.version && ' (stale: the request changed after this decision)'}
          {a.comment && <> — “{a.comment}”</>}
        </li>
      ));

  const decisionButtons = (gate: 2 | 3, role: 'FinanceApprover' | 'D365Admin') =>
    hasAnyRole(user, [role]) &&
    ((gate === 2 && gate2 === 'current') || (gate === 3 && gate3 === 'current')) ? (
      <div className={styles.actions}>
        <ConfirmDialog
          trigger={<Button appearance="primary">Approve gate {gate}</Button>}
          title={`Approve gate ${gate}`}
          confirmLabel="Approve"
          onConfirm={async () => refresh(await api.decide(r.id, { gate, decision: 'Approved' }))}
        >
          <Body1>
            Your approval is recorded against version {r.version}. If the requester changes anything later,
            the request returns to Draft and this approval no longer counts.
          </Body1>
        </ConfirmDialog>
        <ConfirmDialog
          trigger={<Button>Send back</Button>}
          title="Send back to the requester"
          confirmLabel="Send back"
          input={{ kind: 'comment', label: 'What needs to change', required: true }}
          onConfirm={async (comment) =>
            refresh(await api.decide(r.id, { gate, decision: 'SentBack', comment }))
          }
        >
          <Body1>The request returns to Draft. The requester sees your comment.</Body1>
        </ConfirmDialog>
      </div>
    ) : null;

  return (
    <div className={styles.stack}>
      {detail.deviationDetails.length > 0 && ['InReview', 'Approved'].includes(r.status) && (
        <Card className={styles.gate}>
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>
                Differs from the template ({detail.deviationDetails.length}{' '}
                {detail.deviationDetails.length === 1 ? 'item' : 'items'})
              </MessageBarTitle>
              Approving accepts these for {r.legalEntityId ?? 'this venue'} only. Everything else, and the
              template company itself, stays as the template has it.
            </MessageBarBody>
          </MessageBar>
          <DeviationTable details={detail.deviationDetails} />
        </Card>
      )}

      <Card className={styles.gate}>
        <div className={styles.head}>
          <span className={styles.num}>1</span>
          <Subtitle1 as="h2">Automatic validation</Subtitle1>
          <Badge appearance="tint" color={STATE_BADGE[gate1].color}>
            {STATE_BADGE[gate1].text}
          </Badge>
          <span className={styles.spacer} />
          {r.status === 'Draft' && isOwner && hasAnyRole(user, ['Requester']) && (
            <ConfirmDialog
              trigger={
                <Button appearance="primary" disabled={!detail.gate1.passed}>
                  Submit for approval
                </Button>
              }
              title="Submit for approval"
              confirmLabel="Submit"
              onConfirm={async () => refresh(await api.submitRequest(r.id))}
            >
              <Body1>
                Gate 1 runs again now. If it passes, the request goes to the finance controller (gate 2), then
                the D365 administrator (gate 3). Editing afterwards sends it back to Draft.
              </Body1>
            </ConfirmDialog>
          )}
        </div>
        <Body1>
          Answers complete and valid, legal entity ID still free in D365, people and approvals import Valid
          with full routing coverage.
        </Body1>
        {gate1 === 'failed' && (
          <ul className={styles.history}>
            {detail.gate1.reasons.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        )}
      </Card>

      <Card className={styles.gate}>
        <div className={styles.head}>
          <span className={styles.num}>2</span>
          <Subtitle1 as="h2">Finance controller approval</Subtitle1>
          <Badge appearance="tint" color={STATE_BADGE[gate2].color}>
            {STATE_BADGE[gate2].text}
          </Badge>
          <span className={styles.spacer} />
          {decisionButtons(2, 'FinanceApprover')}
        </div>
        <Body1>Reviews the finance setup, deviations from the template and approval limits.</Body1>
        <ul className={styles.history}>{history(2)}</ul>
      </Card>

      <Card className={styles.gate}>
        <div className={styles.head}>
          <span className={styles.num}>3</span>
          <Subtitle1 as="h2">D365 administrator approval</Subtitle1>
          <Badge appearance="tint" color={STATE_BADGE[gate3].color}>
            {STATE_BADGE[gate3].text}
          </Badge>
          <span className={styles.spacer} />
          {decisionButtons(3, 'D365Admin')}
        </div>
        <Body1>
          Confirms the template is ready: last dry run{' '}
          {detail.template.lastDryRunAt ? dateTime(detail.template.lastDryRunAt) : 'never'}
          {detail.dryRunFresh
            ? ', newer than the last template change.'
            : ' — older than the last template change; re-run it first.'}
        </Body1>
        <ul className={styles.history}>{history(3)}</ul>
      </Card>

      <Card className={styles.gate}>
        <div className={styles.head}>
          <span className={styles.num}>4</span>
          <Subtitle1 as="h2">Typed confirmation</Subtitle1>
          <Badge appearance="tint" color={STATE_BADGE[gate4].color}>
            {STATE_BADGE[gate4].text}
          </Badge>
          <span className={styles.spacer} />
          {gate4 === 'current' && hasAnyRole(user, ['D365Admin']) && (
            <ConfirmDialog
              trigger={<Button appearance="primary">Confirm and provision</Button>}
              title={`Provision ${r.legalEntityId ?? ''} in D365`}
              confirmLabel="Start provisioning"
              input={{
                kind: 'typed',
                label: `Type ${r.legalEntityId ?? ''} to confirm`,
                mustEqual: r.legalEntityId ?? '',
              }}
              onConfirm={async (typedId) => {
                await api.confirmRequest(r.id, typedId);
                refresh();
                onStarted();
              }}
            >
              <Body1>
                This creates legal entity <b>{r.legalEntityId}</b>, copies the setup from{' '}
                <b>{detail.template.templateLegalEntityId}</b>, sends the people and approvals package to the
                recurring data job, assigns security scoped to {r.legalEntityId}, and validates the result.
                Gate 1 runs once more first. Answers and people data are frozen as of version {r.version}.
              </Body1>
            </ConfirmDialog>
          )}
        </div>
        <Body1>The D365 administrator types the legal entity ID to start provisioning.</Body1>
        {gate4 === 'current' && api.demoFailure && hasAnyRole(user, ['D365Admin']) && (
          <Switch
            checked={failure.data === 'people'}
            label="Demo option: make the people package fail once (shows Needs attention → Resume)"
            onChange={async (_, d) => {
              await api.demoFailure?.set(d.checked ? 'people' : null);
              void failure.refetch();
            }}
          />
        )}
      </Card>
    </div>
  );
}
